import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import styles from "../Auth/Auth.module.css";

/**
 * TwoFactorSetup Component
 *
 * Provides a multi-step UI for users to enable Time-based One-Time Password (TOTP)
 * authentication. It handles generating a shared secret, displaying a QR code,
 * and verifying the initial setup token.
 */
const TwoFactorSetup = ({ onComplete }) => {
  const [step, setStep] = useState(1);
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const otpRefs = useRef([]);

  // Use the same API base as other auth components
  const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";

  useEffect(() => {
    fetchSetupData();
  }, []);

  /**
   * Fetches the 2FA secret and QR code from the backend.
   */
  const fetchSetupData = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/auth/2fa/setup`,
        {},
        { withCredentials: true }
      );
      if (res.data.success) {
        setQrCode(res.data.qrCode);
        setSecret(res.data.secret);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to initiate 2FA setup");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (e, index) => {
    const val = e.target.value.slice(-1);
    if (isNaN(val)) return;

    const newOtp = [...otp];
    newOtp[index] = val;
    setOtp(newOtp);

    // Auto-focus next input field
    if (val && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    // Handle backspace to return to previous field
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  /**
   * Verifies the 6-digit token entered by the user.
   * If successful, 2FA is permanently enabled for the account.
   */
  const handleVerify = async () => {
    const token = otp.join("");
    if (token.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/auth/2fa/verify`,
        { token },
        { withCredentials: true }
      );
      if (res.data.success) {
        toast.success("2FA Enabled Successfully!");
        if (onComplete) onComplete();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Verification failed. Please try again.");
      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.twoFactorSetupContainer}>
      <h2 className={styles.setupTitle}>Secure Your Account</h2>
      <p className={styles.setupSubtitle}>
        Add an extra layer of security by requiring a code from your phone when you log in.
      </p>

      {step === 1 ? (
        <div className={styles.setupStep}>
          <div className={styles.instructionList}>
            <p>1. Install an authenticator app (Google Authenticator, Authy, or Microsoft Authenticator).</p>
            <p>2. Scan the QR code below or enter the secret key manually.</p>
          </div>

          <div className={styles.qrWrapper}>
            {qrCode ? (
              <img src={qrCode} alt="2FA QR Code" className={styles.qrImage} />
            ) : (
              <div className={styles.qrLoading}>Generating secure key...</div>
            )}
          </div>

          <div className={styles.secretKeyBox}>
            <span className={styles.secretKeyLabel}>Can't scan? Use this code:</span>
            <div className={styles.secretKeyDisplay}>
              <code>{secret}</code>
            </div>
          </div>

          <button
            type="button"
            className={styles.authButton}
            onClick={() => setStep(2)}
            disabled={!qrCode}
          >
            I've scanned it, let's verify
          </button>
        </div>
      ) : (
        <div className={styles.setupStep}>
          <p className={styles.instructionText}>
            Enter the 6-digit code currently shown in your authenticator app to complete the setup.
          </p>

          <div className={styles.otpInputGroup}>
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (otpRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(e, index)}
                onKeyDown={(e) => handleOtpKeyDown(e, index)}
                className={styles.otpDigitInput}
                autoFocus={index === 0}
                disabled={loading}
              />
            ))}
          </div>

          <div className={styles.setupButtonGroup}>
            <button
              type="button"
              className={styles.backBtn}
              onClick={() => setStep(1)}
              disabled={loading}
            >
              Back to QR
            </button>
            <button
              type="button"
              className={styles.authButton}
              onClick={handleVerify}
              disabled={loading || otp.some(d => d === "")}
            >
              {loading ? "Activating..." : "Enable 2FA"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TwoFactorSetup;
